<template>
    <div class="watch">
        <svg class="content" ref="svg">
            <!-- Inner Circle -->
            <circle cx="50%" cy="50%" :r="getPercentStr(INNER_RADIUS)" :stroke="LINE_COLOR" stroke-width="1.5" fill="none" />

            <!-- Circle Divider Lines -->
            <line v-for="line in circleDividerLines" v-bind:key="line.id"
                  :x1="getPercentStr(line.x1)"
                  :y1="getPercentStr(line.y1)"
                  :x2="getPercentStr(line.x2)"
                  :y2="getPercentStr(line.y2)"
                  :style="{'stroke': LINE_COLOR, 'stroke-width': '1.5'}"></line>

            <!-- Threat Level Indicator Texts -->
            <text class="threat-level-text" x="36%" y="69%" >low</text>
            <text class="threat-level-text" x="25%" y="51.5%">med</text>
            <text class="threat-level-text" x="35%" y="32%" >high</text>

            <!-- Center divider line -->
            <line x1="50%" :y1="getPercentStr(50 - INNER_RADIUS - 15)" x2="50%" :y2="getPercentStr(50 + INNER_RADIUS + 15)" :style="{'stroke': LINE_COLOR, 'stroke-width': '1.5'}"></line>

            <!-- Rects -->
            <svg v-for="rect in rects" v-bind:key="rect.index + '-' + rect.type"
                 style="overflow: visible"
                 :x="getPercentStr(rect.position.x)"
                 :y="getPercentStr(rect.position.y)">
                <rect
                      x="0"
                      y="0"
                      :width="getPercentStr(RECT_WIDTH)"
                      :height="getPercentStr(RECT_HEIGHT)"
                      :transform="' rotate(' + rect.rotateAngle + ')'"
                      :style="{'fill': rect.color}"/>
            </svg>

            <!-- Avg Threat Level Text -->
            <text x="45%" y="25%">
                <tspan class="avg-threat-level-text" x="52%" dy="1.2em">Avg threat</tspan>
                <tspan class="avg-threat-level-text" x="52%" dy="1.2em">level</tspan>
                <tspan class="avg-threat-level-text" x="52%" dy="1.2em">{{avgThreatLevel}}</tspan>
            </text>

            <!-- Blurs -->
            <filter id="blurMe">
                <feGaussianBlur in="SourceGraphic" stdDeviation="2" />
            </filter>
            <filter id="textBlur">
                <feGaussianBlur in="SourceGraphic" stdDeviation="0.5" />
            </filter>

            <!-- Progress line -->
            <path class="progress-line" :d="progressBarPath" />

            <!-- Needle Blurred -->
            <line class="needle"
                x1="50%"
                y1="50%"
                :x2="getPercentStr(needlePosition.x2)"
                :y2="getPercentStr(needlePosition.y2)"
                filter="url(#blurMe)"
                :style="{'stroke': NEEDLE_COLOR, 'stroke-width': '3', 'overflow': 'visible'}"></line>

            <!-- Needle -->
            <line
                  x1="50%"
                  y1="50%"
                  :x2="getPercentStr(needlePosition.x2)"
                  :y2="getPercentStr(needlePosition.y2)"
                  :style="{'stroke': NEEDLE_COLOR, 'stroke-width': '3'}"></line>
            <circle cx="50%" cy="50%" :r="getPercentStr(1)" :stroke="NEEDLE_COLOR" stroke-width="1.5" fill="black" />

            <!-- Legend -->
            <text class="malware-type-text virus" x="29%" y="95%" >viruses</text>
            <text class="malware-type-text trojan" x="55%" y="95%">trojans</text>

        </svg>
    </div>
</template>

<script>

    import partialCircle from 'svg-partial-circle'

    const ORIGIN_X = 50; // percent
    const ORIGIN_Y = 50; // percent
    const OUTER_RECT_RADIUS = 37.2; // percent
    const INNER_RECT_RADIUS = 35; // percent
    const INNER_RADIUS = 30; // percent
    const ANGLE_BETWEEN_RECTS = 3; // degree

    const RECT_WIDTH = 0.625; // percent
    const RECT_HEIGHT = 1.6;  // percent

    const VIRUS_BEGIN_INDEX = 1;
    const VIRUS_END_INDEX = 59;
    const VIRUS_INDEX_DIFF = 1;

    const TROJAN_BEGIN_INDEX = -1;
    const TROJAN_END_INDEX = -59;
    const TROJAN_INDEX_DIFF = -1;

    const RECT_COLOR = "#1e2a35";
    const LINE_COLOR = "#1e2a35";
    const NEEDLE_COLOR = "rgba(0, 255, 169, 1)";

    const VIRUS_COLOR = "rgba(255, 0, 67, 1)";
    const TROJAN_COLOR = "rgba(118, 0, 255, 1)";

    function toRadians (angle) {
        return angle * (Math.PI / 180);
    }

    export default {
        name: "threat-watch",
        data() {
            return {
                rects: [],
                circleDividerLines: [],
                avgThreatLevel: "",
                progressBarPath: "",

                RECT_WIDTH: RECT_WIDTH,
                RECT_HEIGHT: RECT_HEIGHT,

                INNER_RADIUS: INNER_RADIUS,
                LINE_COLOR: LINE_COLOR,
                NEEDLE_COLOR: NEEDLE_COLOR,

                needlePosition: {x2:0, y2:0},
                latestProgressPercent: 0
            }
        },
        methods: {
            getPercentStr(value) {
                return value + "%";
            },
            calculateRectPosition(index, radius) {
                var sinMultiplier;
                var cosMultiplier;
                var angle;

                if (index >= 0 && index < 30) {
                    sinMultiplier = 1;
                    cosMultiplier = -1;
                    angle = toRadians(90 - (index * ANGLE_BETWEEN_RECTS));
                }
                else if (index > 30 && index < 60) {
                    sinMultiplier = -1;
                    cosMultiplier = -1;
                    angle = toRadians((index - 30) * ANGLE_BETWEEN_RECTS);
                }
                else if (index < 0 && index > -30) {
                    sinMultiplier = 1;
                    cosMultiplier = 1;
                    angle = toRadians(90 - (index * ANGLE_BETWEEN_RECTS));
                }
                else if (index < -30 && index > -60) {
                    sinMultiplier = -1;
                    cosMultiplier = 1;
                    angle = toRadians((index + 30) * ANGLE_BETWEEN_RECTS);
                }
                else {
                    sinMultiplier = 0;
                    cosMultiplier = 1;
                    angle = toRadians(90);
                }

                var sin = Math.abs(Math.sin(angle));
                var cos = Math.abs(Math.cos(angle));

                // TODO clean up this code
                if (index == 30) {
                    return {
                        x: ORIGIN_X - radius,
                        y: ORIGIN_Y
                    }
                } else if (index == -30) {
                    return {
                        x: ORIGIN_X + radius,
                        y: ORIGIN_Y
                    }
                }

                var x = ORIGIN_X + (cosMultiplier * radius * cos);
                var y = ORIGIN_Y + (sinMultiplier * radius * sin);

                return {
                    x: x,
                    y: y
                }
            },
            calculateRectRotateAngle(index) {
                return index * ANGLE_BETWEEN_RECTS;
            },
            createRects() {
                var newRects = [];

                // Create virus rectangles
                for(let i = VIRUS_BEGIN_INDEX; i <= VIRUS_END_INDEX; i += VIRUS_INDEX_DIFF) {
                    let rotateAngle = this.calculateRectRotateAngle(i);
                    newRects.push({
                        index: i,
                        type: "inner",
                        position: this.calculateRectPosition(i, INNER_RECT_RADIUS),
                        rotateAngle: rotateAngle,
                        color: RECT_COLOR
                    });

                    newRects.push({
                        index: i,
                        type: "outer",
                        position: this.calculateRectPosition(i, OUTER_RECT_RADIUS),
                        rotateAngle: rotateAngle,
                        color: RECT_COLOR
                    });

                }

                // Create trojan rectangles
                for(let i = TROJAN_BEGIN_INDEX; i >= TROJAN_END_INDEX; i += TROJAN_INDEX_DIFF) {
                    let rotateAngle = this.calculateRectRotateAngle(i);
                    newRects.push({
                        index: i,
                        type: "inner",
                        position: this.calculateRectPosition(i, INNER_RECT_RADIUS),
                        rotateAngle: rotateAngle,
                        color: RECT_COLOR
                    });

                    newRects.push({
                        index: i,
                        type: "outer",
                        position: this.calculateRectPosition(i, OUTER_RECT_RADIUS),
                        rotateAngle: rotateAngle,
                        color: RECT_COLOR
                    });

                }

                this.rects = newRects;
            },
            createCircleDividerLines() {
                var xAxisLength = (INNER_RADIUS + 15) * Math.cos(toRadians(30));
                var yAxisLength = (INNER_RADIUS + 15) * Math.sin(toRadians(30));

                this.circleDividerLines.push({
                    id: 1,
                    x1: ORIGIN_X - xAxisLength,
                    y1: ORIGIN_Y - yAxisLength,
                    x2: ORIGIN_X + xAxisLength,
                    y2: ORIGIN_Y + yAxisLength
                });

                this.circleDividerLines.push({
                    id: 2,
                    x1: ORIGIN_X + xAxisLength,
                    y1: ORIGIN_Y - yAxisLength,
                    x2: ORIGIN_X - xAxisLength,
                    y2: ORIGIN_Y + yAxisLength
                });
            },
            setNeedlePosition(percent) {
                var angle = 180 * percent / 100;

                if (percent == 100) {
                    angle = -180;
                }

                this.needlePosition =  {
                    x2: ORIGIN_X - INNER_RADIUS * Math.sin(toRadians(angle)),
                    y2: ORIGIN_Y + INNER_RADIUS * Math.cos(toRadians(angle))
                }
            },
            setProgressBar(percent) {
                this.latestProgressPercent = percent;
                var angle = 180 * percent / 100;
                var svgBox = this.$refs.svg.getBoundingClientRect();

                var width = svgBox.width;
                var height = svgBox.height;

                this.progressBarPath = partialCircle(
                    width / 2, height / 2,         // center X and Y
                    INNER_RADIUS * width / 100 ,             // radius
                    Math.PI / 2,    // start angle
                    toRadians(angle) + Math.PI / 2 // end angle
                )
                .map((command) => command.join(' '))
                .join(' ');
            },
            setAverageThreatLevel(avgThreatLevel) {
                this.avgThreatLevel = avgThreatLevel;
                this.setNeedlePosition(avgThreatLevel);
                this.setProgressBar(avgThreatLevel);
            },
            clearRects() {
                for(let i = 0; i < this.rects.length; i++) {
                    let rect = this.rects[i];
                    rect.color = RECT_COLOR;
                }
            },
            setVirusPercent(percent) {
                var maxIndex = Math.floor(percent / 100 * 60);

                for(let i = 0; i < this.rects.length; i++) {
                    let rect = this.rects[i];
                    if (rect.index > 0 && rect.index <= maxIndex) {
                        rect.color = VIRUS_COLOR;
                    }
                }
            },
            setTrojanPercent(percent) {
                var minIndex = -Math.floor(percent / 100 * 60);

                for(let i = 0; i < this.rects.length; i++) {
                    let rect = this.rects[i];
                    if (rect.index < 0 && rect.index >= minIndex) {
                        rect.color = TROJAN_COLOR;
                    }
                }
            },
            updateValues(virusPercent, trojanPercent, avgThreatLevel) {
                this.setAverageThreatLevel(avgThreatLevel);
                this.clearRects();
                this.setVirusPercent(virusPercent);
                this.setTrojanPercent(trojanPercent);
            },
            handleResize() {
                this.setProgressBar(this.latestProgressPercent);
            }
        },
        beforeDestroy: function () {
            window.removeEventListener('resize', this.handleResize);
        },
        mounted() {
            window.addEventListener('resize', this.handleResize);
            this.createRects();
            this.createCircleDividerLines();
            this.updateValues(0, 0, 0);
        }
    }
</script>

<style scoped>

    .watch {
        position: relative;
        width: 100%;
    }

    .watch:after {
        content: "";
        display: block;
        padding-bottom: 100%;
    }

    .content {
        position: absolute;
        width: 100%;
        height: 100%;
        background-color:transparent;
    }

    .threat-level-text {
        fill: rgba(73, 82, 91, 1);
        font-size: 0.8vw;
        font-family: 'Digital-Serial-Bold';
    }

    .malware-type-text {
        font-size: 0.8vw;
        font-family: 'Digital-Serial-Bold';
    }

    .virus {
        fill: rgba(255, 0, 67, 1);
    }

    .trojan {
        fill: rgba(118, 0, 255, 1);
    }

    .avg-threat-level-text {
        text-shadow: 0px 0px 6px  rgba(0, 255, 169, 0.5);
        font-family: 'Digital-Serial-Regular';
        font-size: 0.8vw;
        font-weight: bold;
        fill: rgba(0, 255, 169, 1);
        text-align: left;
    }

    .progress-line {
        fill:none;
        stroke: rgba(47, 58, 68, 1);
        stroke-width: 0.7em;
    }

    @media(max-width:1600px) {
        .avg-threat-level-text {
            font-size: 13px;
        }
        .threat-level-text {
            font-size: 12px;
        }
        .malware-type-text {
            font-size: 10px;
        }
    }



</style>



// WEBPACK FOOTER //
// src/components/threat-watch/ThreatWatch.vue